import { type ComponentType, lazy, useRef } from "react";

export type PreloadableComponent<P extends object> = ComponentType<P> & {
  /** Baixa e avalia o módulo; o próximo render não suspende. */
  preload: () => Promise<unknown>;
};

/**
 * `React.lazy` que não suspende quando o módulo já foi pré-carregado (SA-1452).
 *
 * O `React.lazy` suspende no primeiro render mesmo com o chunk já baixado (a
 * promise só resolve no próximo microtask), e o React 19 segura a revelação de
 * um Suspense por ~300 ms depois de mostrar o fallback. No F5 da tela de
 * conversas eram dois desses em série (providers autenticados, layout), ~600 ms
 * de espinner com tudo já em memória. Pré-carregado, o componente real
 * renderiza direto; sem pré-carga, é o `lazy` de sempre. A escolha fica fixa
 * por montagem (trocar o tipo do componente remontaria a árvore).
 */
export function lazyWithPreload<P extends object>(
  loader: () => Promise<{ default: ComponentType<P> }>
): PreloadableComponent<P> {
  let loaded: ComponentType<P> | undefined;
  let pending: Promise<{ default: ComponentType<P> }> | undefined;

  const preload = () => {
    pending ??= loader().then(
      (module) => {
        loaded = module.default;
        return module;
      },
      (error: unknown) => {
        // Falhou (rede caindo, chunk de um deploy anterior): a próxima chamada
        // tenta de novo, inclusive a do `lazy` no render, como o React.lazy
        // sem pré-carga faria.
        pending = undefined;
        throw error;
      }
    );
    return pending;
  };

  const LazyComponent = lazy(preload);

  function Preloadable(props: P) {
    const Component = useRef<ComponentType<P>>(loaded ?? LazyComponent).current;
    return <Component {...props} />;
  }

  return Object.assign(Preloadable, { preload });
}
